import { extractHexColors } from './themeTools';

export async function extractPaletteFromImage(file: File): Promise<string[]> {
  const image = await loadImage(file);
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) {
    return [];
  }

  const maxSize = 160;
  const ratio = Math.min(maxSize / image.width, maxSize / image.height, 1);
  canvas.width = Math.max(1, Math.round(image.width * ratio));
  canvas.height = Math.max(1, Math.round(image.height * ratio));
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
  const buckets = new Map<string, { color: string; count: number; saturation: number }>();

  for (let index = 0; index < data.length; index += 16) {
    const alpha = data[index + 3];
    if (alpha < 180) {
      continue;
    }

    const r = data[index];
    const g = data[index + 1];
    const b = data[index + 2];
    const saturation = getSaturation(r, g, b);
    const lightness = Math.max(r, g, b) / 255;
    if (lightness > 0.97 || lightness < 0.03) {
      continue;
    }

    const bucket = toHex(roundChannel(r), roundChannel(g), roundChannel(b));
    const current = buckets.get(bucket) || { color: bucket, count: 0, saturation };
    current.count += saturation > 0.12 ? 3 : 1;
    current.saturation = Math.max(current.saturation, saturation);
    buckets.set(bucket, current);
  }

  const palette = [...buckets.values()]
    .sort((a, b) => (b.count + b.saturation * 100) - (a.count + a.saturation * 100))
    .map((entry) => entry.color)
    .slice(0, 8);

  return palette.length > 0 ? palette : extractHexColors('');
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Unable to read image.'));
    };
    image.src = url;
  });
}

function roundChannel(value: number): number {
  return Math.round(value / 24) * 24;
}

function toHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((value) => Math.max(0, Math.min(255, value)).toString(16).padStart(2, '0')).join('')}`;
}

function getSaturation(r: number, g: number, b: number): number {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max === 0) {
    return 0;
  }
  return (max - min) / max;
}
